/**
 * ERGO 360 - 101 Carousel Section
 *
 * 功能：
 * 1. 4个全屏 slider，视频背景
 * 2. 当 slider 激活时视频播放
 * 3. 底部进度条跟随视频播放进度
 * 4. 视频播完自动切换下一个 slider
 * 5. 支持手动箭头切换
 * 6. 内容逐行进场动画（GSAP）
 */

class Ergo360_101 {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.slideCount = parseInt(container.dataset.slideCount, 10) || 4;

    // 当前激活的 slide 索引
    this.currentIndex = 0;

    // 是否正在切换中（防止快速连续点击）
    this.isTransitioning = false;

    // 进度条动画帧 ID
    this.progressAnimationId = null;

    // 缓存 DOM 元素
    this.videos = container.querySelectorAll('.ergo-360-101__video');
    this.slides = container.querySelectorAll('.ergo-360-101__slide');
    this.progressBars = container.querySelectorAll('.ergo-360-101__progress-bar');
    this.prevButton = container.querySelector('.ergo-360-101__arrow--prev');
    this.nextButton = container.querySelector('.ergo-360-101__arrow--next');

    // 入场动画元素
    this.logo = container.querySelector('.ergo-360-101__logo');
    this.subtitle = container.querySelector('.ergo-360-101__subtitle');
    this.nav = container.querySelector('.ergo-360-101__nav');
    this.progress = container.querySelector('.ergo-360-101__progress');

    // 初始化
    this.init();
  }

  init() {
    // 等待 GSAP 加载
    this.waitForGSAP().then(() => {
      // 设置视频事件监听
      this.setupVideoListeners();

      // 绑定箭头按钮事件
      this.bindArrowEvents();

      // 使用 IntersectionObserver 监听 section 进入视口
      this.setupIntersectionObserver();
    });
  }

  /**
   * 等待 GSAP 加载完成
   */
  waitForGSAP() {
    return new Promise((resolve) => {
      if (typeof gsap !== 'undefined') {
        resolve();
        return;
      }

      const checkGSAP = setInterval(() => {
        if (typeof gsap !== 'undefined') {
          clearInterval(checkGSAP);
          resolve();
        }
      }, 100);

      // 5秒超时保护
      setTimeout(() => {
        clearInterval(checkGSAP);
        resolve();
      }, 5000);
    });
  }

  /**
   * 设置 IntersectionObserver 监听 section 进入/离开视口
   */
  setupIntersectionObserver() {
    // 标记是否已初始化
    this.hasInitialized = false;

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.onSectionEnter();
          } else {
            this.onSectionLeave();
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -50% 0px', // section 顶边进入视窗 50% 位置时触发
      }
    );

    this.visibilityObserver.observe(this.container);
  }

  /**
   * Section 进入视口
   */
  onSectionEnter() {
    if (!this.hasInitialized) {
      this.hasInitialized = true;
      // 预加载第一个视频
      const firstVideo = this.videos[0];
      if (firstVideo) {
        this.preloadVideo(firstVideo, 'auto');
      }
      // 播放入场动画序列，完成后切换到第一个 slide
      this.playSectionEntryAnimation();
    } else {
      const currentVideo = this.videos[this.currentIndex];
      if (currentVideo) {
        currentVideo.play().catch(() => {});
      }
      this.startProgressAnimation();
    }
  }

  /**
   * 播放 section 入场动画序列
   * 顺序：logo → 副标题 → slide 内容 → 导航和进度条
   * 视频直接播放，不参与入场动画
   */
  playSectionEntryAnimation() {
    // 视频直接播放（不参与入场动画）
    const firstVideo = this.videos[0];
    if (firstVideo) {
      firstVideo.classList.add('is-active');
      firstVideo.play().catch(() => {});
    }

    // 激活第一个 slide（内容动画稍后播放）
    const firstSlide = this.slides[0];
    if (firstSlide) {
      firstSlide.classList.add('is-active');
    }

    // 启动进度条
    this.startProgressAnimation();
    this.updateArrowStates();

    if (typeof gsap === 'undefined') {
      // GSAP 未加载，直接显示所有内容
      [this.logo, this.subtitle, this.nav, this.progress].forEach(el => {
        if (el) el.style.opacity = '1';
      });
      return;
    }

    const tl = gsap.timeline();

    // 1. Logo 从下淡入
    if (this.logo) {
      tl.fromTo(
        this.logo,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }
      );
    }

    // 2. 副标题从下淡入
    if (this.subtitle) {
      tl.fromTo(
        this.subtitle,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
        '-=0.4'
      );
    }

    // 3. 第一个 slide 的内容行动画
    if (firstSlide) {
      const rows = firstSlide.querySelectorAll('.ergo-360-101__row');
      rows.forEach((row, i) => {
        tl.fromTo(
          row,
          { opacity: 0, y: 20 + i * 10 },
          { opacity: 1, y: 0, duration: 0.6 + i * 0.1, ease: 'power2.out' },
          i === 0 ? '-=0.3' : '-=0.4'
        );
      });
    }

    // 4. 导航箭头和进度条同时淡入
    const navAndProgress = [this.nav, this.progress].filter(Boolean);
    if (navAndProgress.length > 0) {
      tl.fromTo(
        navAndProgress,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
        '-=0.3'
      );
    }
  }

  /**
   * Section 离开视口
   */
  onSectionLeave() {
    this.videos.forEach((video) => {
      video.pause();
    });
    this.stopProgressAnimation();
  }

  /**
   * 预加载指定视频
   * @param {HTMLVideoElement} video - 视频元素
   * @param {string} preloadValue - 预加载值 ('auto' 或 'metadata')
   */
  preloadVideo(video, preloadValue = 'auto') {
    if (!video) return;

    // 如果使用 data-src 懒加载模式，先设置 src
    if (video.dataset.src && !video.src) {
      video.src = video.dataset.src;
    }

    if (video.preload === 'none') {
      video.preload = preloadValue;
      video.load();
    }
  }

  /**
   * 预加载下一个视频
   * @param {number} currentIndex - 当前视频索引
   */
  preloadNextVideo(currentIndex) {
    const nextIndex = currentIndex + 1;
    if (nextIndex < this.slideCount) {
      const nextVideo = this.videos[nextIndex];
      // 移动端使用 metadata，桌面端使用 auto
      const preloadValue = window.innerWidth < 768 ? 'metadata' : 'auto';
      this.preloadVideo(nextVideo, preloadValue);
    }
  }

  /**
   * 设置视频事件监听
   */
  setupVideoListeners() {
    this.videos.forEach((video, index) => {
      // 视频播放结束 - 切换到下一个（非最后一张时）
      video.addEventListener('ended', () => {
        // 非循环模式：最后一张不自动切换
        if (this.currentIndex < this.slideCount - 1) {
          this.nextSlide();
        }
      });

      // 视频加载错误处理 - 5秒后自动切换
      video.addEventListener('error', () => {
        if (index === this.currentIndex) {
          setTimeout(() => {
            if (this.currentIndex === index) {
              this.nextSlide();
            }
          }, 5000);
        }
      });
    });
  }

  /**
   * 绑定箭头按钮事件
   */
  bindArrowEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        // GA4 埋点：轮播导航切换
        if (window.Ergo360Analytics && this.currentIndex > 0) {
          window.Ergo360Analytics.carouselNavigate('prev', this.currentIndex, this.currentIndex - 1);
        }
        this.prevSlide();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        // GA4 埋点：轮播导航切换
        if (window.Ergo360Analytics && this.currentIndex < this.slideCount - 1) {
          window.Ergo360Analytics.carouselNavigate('next', this.currentIndex, this.currentIndex + 1);
        }
        this.nextSlide();
      });
    }

    // 键盘事件支持
    document.addEventListener('keydown', (e) => {
      // 只有当 section 在视口中时才响应键盘
      const rect = this.container.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (!isVisible) return;

      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    });
  }

  /**
   * 切换到指定 slide
   * @param {number} index - 目标 slide 索引
   * @param {boolean} isFirst - 是否是首次加载
   */
  goToSlide(index, isFirst = false) {
    // 防止重复切换
    if (this.isTransitioning || index === this.currentIndex && !isFirst) {
      return;
    }

    this.isTransitioning = true;

    // 暂停所有视频并重置
    this.videos.forEach((video) => {
      video.pause();
      video.currentTime = 0;
      video.classList.remove('is-active');
    });

    // 隐藏所有 slides
    this.slides.forEach((slide) => {
      slide.classList.remove('is-active');
    });

    // 更新进度条状态
    this.progressBars.forEach((bar, i) => {
      const fill = bar.querySelector('.ergo-360-101__progress-fill');
      bar.classList.remove('is-complete', 'is-past');

      if (i < index) {
        // 已播放完的进度条
        bar.classList.add('is-past');
        fill.style.width = '100%';
      } else if (i === index) {
        // 当前进度条 - 从 0 开始
        fill.style.width = '0%';
      } else {
        // 未播放的进度条
        fill.style.width = '0%';
      }
    });

    // 更新当前索引
    this.currentIndex = index;

    // 激活当前视频
    const currentVideo = this.videos[index];
    if (currentVideo) {
      currentVideo.classList.add('is-active');

      // 确保当前视频已预加载
      this.preloadVideo(currentVideo, 'auto');

      // 播放视频
      const playPromise = currentVideo.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          // GA4 埋点：轮播视频播放
          if (window.Ergo360Analytics) {
            const slideTitle = this.slides[index]?.querySelector('.ergo-360-101__title')?.textContent?.trim() || '';
            window.Ergo360Analytics.carouselVideoPlay(index, slideTitle);
          }
        }).catch(() => {
          // 如果自动播放被阻止，5秒后自动切换
          setTimeout(() => {
            if (this.currentIndex === index) {
              this.nextSlide();
            }
          }, 5000);
        });
      }
    }

    // 智能预加载：预加载下一个视频
    this.preloadNextVideo(index);

    // 激活当前 slide
    const currentSlide = this.slides[index];
    if (currentSlide) {
      currentSlide.classList.add('is-active');

      // 播放内容进场动画
      this.playEntryAnimation(currentSlide);
    }

    // 启动进度条动画
    this.startProgressAnimation();

    // 更新箭头按钮状态
    this.updateArrowStates();

    // 重置过渡状态
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);
  }

  /**
   * 启动进度条动画循环
   */
  startProgressAnimation() {
    // 取消之前的动画
    this.stopProgressAnimation();

    const animate = () => {
      const video = this.videos[this.currentIndex];
      const progressBar = this.progressBars[this.currentIndex];

      if (video && progressBar && video.duration && !isNaN(video.duration)) {
        const progress = (video.currentTime / video.duration) * 100;
        const fill = progressBar.querySelector('.ergo-360-101__progress-fill');
        if (fill) {
          fill.style.width = `${progress}%`;
        }
      }

      // 继续下一帧
      this.progressAnimationId = requestAnimationFrame(animate);
    };

    this.progressAnimationId = requestAnimationFrame(animate);
  }

  /**
   * 停止进度条动画循环
   */
  stopProgressAnimation() {
    if (this.progressAnimationId) {
      cancelAnimationFrame(this.progressAnimationId);
      this.progressAnimationId = null;
    }
  }

  /**
   * 播放内容进场动画
   * @param {HTMLElement} slide - 当前 slide 元素
   */
  playEntryAnimation(slide) {
    const rows = slide.querySelectorAll('.ergo-360-101__row');

    if (typeof gsap !== 'undefined') {
      // 使用 GSAP 动画
      rows.forEach((row, i) => {
        // 规律：每行 Y+10px, duration+0.1s, delay+0.1s
        const yOffset = 20 + i * 10;
        const duration = 0.6 + i * 0.1;
        const delay = i * 0.1;

        gsap.fromTo(
          row,
          {
            y: yOffset,
            opacity: 0
          },
          {
            y: 0,
            opacity: 1,
            duration: duration,
            delay: delay,
            ease: 'power2.out'
          }
        );
      });
    } else {
      // 降级方案：使用 CSS 动画
      rows.forEach((row, i) => {
        row.style.transition = `opacity ${0.6 + i * 0.1}s ease-out, transform ${0.6 + i * 0.1}s ease-out`;
        row.style.transitionDelay = `${i * 0.1}s`;

        // 强制重绘
        row.offsetHeight;

        row.style.opacity = '1';
        row.style.transform = 'translateY(0)';
      });
    }
  }

  /**
   * 切换到下一个 slide（非循环）
   */
  nextSlide() {
    // 非循环模式：最后一张时不切换
    if (this.currentIndex >= this.slideCount - 1) {
      return;
    }
    this.goToSlide(this.currentIndex + 1);
  }

  /**
   * 切换到上一个 slide（非循环）
   */
  prevSlide() {
    // 非循环模式：第一张时不切换
    if (this.currentIndex <= 0) {
      return;
    }
    this.goToSlide(this.currentIndex - 1);
  }

  /**
   * 更新箭头按钮状态（禁用/启用）
   */
  updateArrowStates() {
    // 第一张时禁用 prev 按钮
    if (this.prevButton) {
      if (this.currentIndex <= 0) {
        this.prevButton.style.opacity = '0.3';
        this.prevButton.style.pointerEvents = 'none';
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.style.opacity = '1';
        this.prevButton.style.pointerEvents = 'auto';
        this.prevButton.removeAttribute('disabled');
      }
    }

    // 最后一张时禁用 next 按钮
    if (this.nextButton) {
      if (this.currentIndex >= this.slideCount - 1) {
        this.nextButton.style.opacity = '0.3';
        this.nextButton.style.pointerEvents = 'none';
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.style.opacity = '1';
        this.nextButton.style.pointerEvents = 'auto';
        this.nextButton.removeAttribute('disabled');
      }
    }
  }

  /**
   * 销毁实例
   */
  destroy() {
    // 停止进度条动画
    this.stopProgressAnimation();

    // 暂停所有视频
    this.videos.forEach((video) => {
      video.pause();
      video.removeAttribute('src');
      video.load();
    });

    // 清理视口监听
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
  }
}

// ============================================
// 初始化
// ============================================

document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.ergo-360-101');

  containers.forEach((container) => {
    new Ergo360_101(container);
  });
});

// Shopify Section Rendering API 支持
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.ergo-360-101');
    if (container) {
      new Ergo360_101(container);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target.querySelector('.ergo-360-101');
    if (container && container._ergo360_101Instance) {
      container._ergo360_101Instance.destroy();
    }
  });
}
